import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Button } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { partitionStayPlanContracts, useStayPlanContracts } from "@/hooks/useStayPlanContracts";
import { type StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { PlanCard } from "@/components/packages/plan-card/PlanCard";
import { PlanHistoryRow } from "@/components/packages/plan-card/PlanHistoryRow";
import { StayPlanContractDialog } from "@/pages/daycare-hotel/plans/StayPlanContractDialog";
import { useContractStayPlan } from "@/pages/daycare-hotel/plans/useContractStayPlan";
import type { StayPlanContractDraft } from "@/pages/daycare-hotel/plans/stayPlanContractDraft";
import { useLocation, useNavigate } from "react-router-dom";

export interface StayPlansSectionProps {
  clinicId: string;
  ownerId: string;
  petId: string;
  petName: string;
  hasDaycare: boolean;
  hasHotel: boolean;
  canContract: boolean;
}

const PAGE_SIZE = 10;

export function StayPlansSection({ clinicId, ownerId, petId, petName, hasDaycare, hasHotel, canContract }: StayPlansSectionProps) {
  const { t } = useTranslation("patients");
  const navigate = useNavigate();
  const location = useLocation();
  const [contractOpen, setContractOpen] = useState(false);
  const [contractModule, setContractModule] = useState<StayPlanModule | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [openHistoryId, setOpenHistoryId] = useState<string | null>(null);
  const daycare = useStayPlanContracts("daycare", clinicId, [petId]);
  const hotel = useStayPlanContracts("hotel", clinicId, [petId]);
  const contractPlan = useContractStayPlan();
  const modules = useMemo(() => [hasDaycare ? "daycare" : null, hasHotel ? "hotel" : null].filter(Boolean) as StayPlanModule[], [hasDaycare, hasHotel]);
  const contracts = useMemo(() => modules.flatMap((module) => module === "daycare" ? daycare.data ?? [] : hotel.data ?? []).sort((a, b) => b.startsOn.localeCompare(a.startsOn)), [modules, daycare.data, hotel.data]);
  const groups = partitionStayPlanContracts(contracts);
  const open = [...groups.active, ...groups.paused];
  const history = groups.history.slice((historyPage - 1) * PAGE_SIZE, historyPage * PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(groups.history.length / PAGE_SIZE));
  const title = hasDaycare && hasHotel ? "stayPlans.titleDaycareHotel" : hasDaycare ? "stayPlans.titleDaycare" : "stayPlans.titleHotel";
  const today = new Date().toLocaleDateString("en-CA");
  const isLoading = (hasDaycare && daycare.isLoading) || (hasHotel && hotel.isLoading);
  const submit = async (draft: StayPlanContractDraft) => {
    const { saleId } = await contractPlan.mutateAsync({ draft, petId, ownerId });
    setContractOpen(false);
    if (draft.payNow)
      navigate("/sales/new", {
        state: {
          resumeSale: {
            saleId,
            context: "planCharge",
            returnTo: `${location.pathname}${location.search}`,
          },
        },
      });
  };

  return (
    <section
      data-testid="stay-plans-section"
      className="rounded-xl border border-border bg-cream-50 p-4 shadow-akuna-sm sm:p-6 lg:p-8"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 className="font-display text-[1.375rem] font-bold tracking-tight text-foreground">{t(title)}</h3>
        </div>
        {canContract ? (
          <Button type="button" onClick={() => { setContractModule(hasDaycare && hasHotel ? null : hasDaycare ? "daycare" : "hotel"); setContractOpen(true); }}>
            <Plus className="me-2 h-4 w-4" aria-hidden />
            {t("daycarePlans.contractPlan")}
          </Button>
        ) : null}
      </div>

      {isLoading ? <Skeleton className="mt-6 h-32 w-full" /> : <>
        <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-color-light">{t("stayPlans.active")}</p>
        {open.length ? (
          <div className="mt-2.5 space-y-4">
            {open.map((contract, index) => (
              <PlanCard
                key={`${contract.module}-${contract.id}`}
                contract={contract}
                petName={petName}
                compact={index > 0}
              />
            ))}
          </div>
        ) : (
          <p className="mt-2.5 rounded-lg border border-border bg-background px-4 py-4 text-sm text-muted-foreground">
            {t("stayPlans.emptyActive")}
          </p>
        )}

        <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-color-light">{t("stayPlans.history")}</p>
        {history.length ? (
          <div className="mt-2.5 space-y-3">
            <div className="overflow-hidden rounded-lg border border-border">
              {history.map((contract) => {
                const key = `${contract.module}-${contract.id}`;
                return (
                  <PlanHistoryRow
                    key={key}
                    contract={contract}
                    expanded={openHistoryId === key}
                    onToggle={() => setOpenHistoryId((current) => (current === key ? null : key))}
                  />
                );
              })}
            </div>
            {history
              .filter((contract) => openHistoryId === `${contract.module}-${contract.id}`)
              .map((contract) => (
                <PlanCard key={`detail-${contract.module}-${contract.id}`} contract={contract} petName={petName} />
              ))}
          </div>
        ) : (
          <p className="mt-2.5 rounded-lg border border-border bg-background px-4 py-4 text-sm text-muted-foreground">
            {t("stayPlans.emptyHistory")}
          </p>
        )}

        {groups.history.length > PAGE_SIZE ? (
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" disabled={historyPage === 1} onClick={() => setHistoryPage((page) => page - 1)}>{t("stayPlans.previousPage")}</Button>
            <span className="text-sm text-muted-foreground">{t("stayPlans.page", { current: historyPage, total: pageCount })}</span>
            <Button type="button" variant="outline" size="sm" disabled={historyPage === pageCount} onClick={() => setHistoryPage((page) => page + 1)}>{t("stayPlans.nextPage")}</Button>
          </div>
        ) : null}
      </>}

      {contractOpen ? <StayPlanContractDialog open={contractOpen} onOpenChange={(open) => { setContractOpen(open); if (!open) setContractModule(null); }} module={contractModule} availableModules={modules} defaultStartsOn={today} submitting={contractPlan.isPending} onSubmit={submit} /> : null}
    </section>
  );
}
